import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, type Ref, useEffect, useRef, useState } from "react";
import { pickBookCover } from "#/lib/assets";
import { useExclusiveSlugs } from "#/lib/exclusive";
import { formatMoney } from "#/lib/medusa-types/commerce";
import { ROUTES } from "#/lib/routes";
import { useDebouncedCallback } from "#/lib/useDebouncedCallback";
import { cn } from "#/lib/utils";
import { type ProductFilters, useProducts } from "#/queries/products";
import { useCategories } from "#/queries/taxonomy";
import { useUnlockStore } from "#/store/unlock";

const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_MIN_QUERY_LEN = 2;
const DEFAULT_RESULT_LIMIT = 6;

function SearchSpinner({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      role="status"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className={cn("animate-spin", className)}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export interface SearchBarProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit?: (q: string) => void;
  onSuggestionPicked?: () => void;
  /** Restrict suggestions to a context (e.g. a collection's publisher, the
   * shop's active facets, or the sale subset). Omit for a catalog-wide
   * search. */
  scope?: Pick<ProductFilters, "categorySlugs" | "onSale">;
  placeholder?: string;
  suggestionsPosition?: "popover" | "inline";
  className?: string;
  inputClassName?: string;
  suggestionsClassName?: string;
  popoverClassName?: string;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  idleHint?: string;
  resultLimit?: number;
  debounceMs?: number;
  minQueryLen?: number;
  inputRef?: Ref<HTMLInputElement>;
  ariaLabel?: string;
}

export function SearchBar({
  value,
  onChange,
  onSubmit,
  onSuggestionPicked,
  scope,
  placeholder = "Search books...",
  suggestionsPosition = "popover",
  className,
  inputClassName,
  suggestionsClassName,
  popoverClassName,
  leftSlot,
  rightSlot,
  idleHint,
  resultLimit = DEFAULT_RESULT_LIMIT,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minQueryLen = DEFAULT_MIN_QUERY_LEN,
  inputRef,
  ariaLabel,
}: SearchBarProps) {
  const navigate = useNavigate();
  const [debounced, setDebounced] = useState(value.trim());
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { trigger: commitDebounced, cancel: cancelDebounced } =
    useDebouncedCallback((v: string) => setDebounced(v.trim()), debounceMs);

  useEffect(() => {
    if (value === "") {
      cancelDebounced();
      setDebounced("");
    } else {
      commitDebounced(value);
    }
  }, [value, commitDebounced, cancelDebounced]);

  const activeQ = debounced.length >= minQueryLen ? debounced : undefined;

  const { data, isFetching } = useProducts({
    q: activeQ,
    limit: resultLimit,
    categorySlugs: scope?.categorySlugs,
    onSale: scope?.onSale,
  });
  const { data: categories } = useCategories();
  const unlockedSlugs = useUnlockStore((s) => s.unlockedSlugs);
  const exclusiveSlugs = useExclusiveSlugs();

  const lockedExclusiveCategoryIds = new Set(
    (categories ?? [])
      .filter(
        (c) => exclusiveSlugs.has(c.slug) && !unlockedSlugs.includes(c.slug),
      )
      .map((c) => c.id),
  );

  const items = activeQ
    ? (data?.items ?? []).filter(
        (p) => !lockedExclusiveCategoryIds.has(p.metadata.categoryId),
      )
    : [];
  const total = activeQ ? (data?.total ?? 0) : 0;

  const safeActiveIndex =
    activeIndex >= 0 && activeIndex < items.length ? activeIndex : -1;

  useEffect(() => {
    if (suggestionsPosition !== "popover") return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [suggestionsPosition]);

  const submit = (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < minQueryLen) return;
    if (onSubmit) onSubmit(trimmed);
    else navigate({ to: ROUTES.shop, search: { q: trimmed, page: 1 } });
    setFocused(false);
    setActiveIndex(-1);
  };

  const pick = (slug: string) => {
    navigate({ to: ROUTES.product(slug) });
    setFocused(false);
    setActiveIndex(-1);
    onSuggestionPicked?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const picked = safeActiveIndex >= 0 ? items[safeActiveIndex] : undefined;
      if (picked) pick(picked.metadata.slug);
      else submit(value);
    } else if (e.key === "ArrowDown" && items.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp" && items.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Escape" && suggestionsPosition === "popover") {
      setFocused(false);
    }
  };

  const showInlineIdle =
    suggestionsPosition === "inline" && !activeQ && idleHint !== undefined;
  const trimmedLen = value.trim().length;
  const showInlineShortHint =
    suggestionsPosition === "inline" &&
    !activeQ &&
    trimmedLen > 0 &&
    trimmedLen < minQueryLen;

  // True while the user is typing but the debounce hasn't fired yet, OR while
  // a fetch is in flight — either way the displayed results are stale.
  const queryPending = trimmedLen >= minQueryLen && value.trim() !== debounced;
  const isSearching = Boolean(activeQ) && (isFetching || queryPending);
  const showLoadingHint = isSearching && items.length === 0;

  const popoverOpen =
    suggestionsPosition === "popover" && focused && Boolean(activeQ);
  const showSuggestions =
    suggestionsPosition === "inline" ? Boolean(activeQ) : popoverOpen;

  const suggestions = (
    <>
      {showLoadingHint ? (
        <div className="flex items-center justify-center gap-2 px-5 py-8 text-muted-foreground text-sm">
          <SearchSpinner />
          <span>Searching…</span>
        </div>
      ) : activeQ && items.length === 0 && !isFetching ? (
        <div className="px-5 py-8 text-center text-muted-foreground text-sm">
          No products match &ldquo;{activeQ}&rdquo;
        </div>
      ) : null}
      {items.length > 0 ? (
        <ul className="m-0 list-none p-0 py-2">
          {items.map((p, i) => {
            const cover =
              p.thumbnail ?? p.images?.[0]?.url ?? pickBookCover(p.id);
            const active = i === safeActiveIndex;
            return (
              <li key={p.id}>
                <Link
                  to={ROUTES.product(p.metadata.slug)}
                  onClick={() => {
                    setFocused(false);
                    setActiveIndex(-1);
                    onSuggestionPicked?.();
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={cn(
                    "flex items-center gap-3 px-5 py-2.5 no-underline transition-colors",
                    active ? "bg-muted" : "hover:bg-muted",
                  )}
                >
                  <img
                    src={cover}
                    alt=""
                    className="h-12 w-auto shrink-0 rounded-md object-contain"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-1 font-semibold text-foreground text-sm">
                      {p.title}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {p.metadata.publisher ?? "Learning Plus"}
                    </div>
                  </div>
                  <div className="shrink-0 font-medium text-foreground text-sm">
                    {formatMoney(p.metadata.priceAmount, p.metadata.currency)}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
      {activeQ && total > items.length ? (
        <button
          type="button"
          onClick={() => submit(value)}
          className="w-full cursor-pointer border-border border-t bg-transparent px-5 py-3 text-left font-medium text-primary text-sm hover:bg-muted"
        >
          View all {total} results for &ldquo;{activeQ}&rdquo;
        </button>
      ) : null}
    </>
  );

  return (
    <>
      <div
        ref={containerRef}
        className={cn(!leftSlot && "relative", className)}
      >
        {leftSlot ? (
          isSearching ? (
            <SearchSpinner className="size-5 shrink-0 text-muted-foreground" />
          ) : (
            leftSlot
          )
        ) : null}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-busy={isSearching || undefined}
          className={inputClassName}
        />
        {!leftSlot && isSearching ? (
          <SearchSpinner
            className={cn(
              "-translate-y-1/2 pointer-events-none absolute top-1/2 text-muted-foreground",
              rightSlot ? "right-16" : "right-4",
            )}
          />
        ) : null}
        {rightSlot}
        {popoverOpen ? (
          <div
            className={cn(
              "absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
              popoverClassName,
            )}
          >
            {suggestions}
          </div>
        ) : null}
      </div>
      {suggestionsPosition === "inline" ? (
        <div className={suggestionsClassName}>
          {showInlineShortHint ? (
            <div className="px-5 py-12 text-center text-muted-foreground text-sm">
              Type at least {minQueryLen} characters
            </div>
          ) : showInlineIdle ? (
            <div className="px-5 py-12 text-center text-muted-foreground text-sm">
              {idleHint}
            </div>
          ) : showSuggestions ? (
            suggestions
          ) : null}
        </div>
      ) : null}
    </>
  );
}
